/**
 * ChangePasswordForm — single-step change password form.
 *
 * Three fields in sequence: old password → new password → confirm password.
 * New password field shows PasswordStrengthBars + PasswordRequirementList.
 * Confirm password shows inline error when it doesn't match.
 * Eye icon toggle on all three password fields.
 * Submit button disabled when form invalid or submitting.
 * On success: show success message for 2s, then call onSuccess().
 * On error: show inline error message.
 *
 * @param onSubmit — called with (oldPassword, newPassword) on valid submit
 * @param onSuccess — called after 2s success timer completes
 * @param isRTL — enables RTL layout
 * @param translate — scoped i18n function (auth namespace)
 */
import {
  Eye,
  EyeOff,
  Check,
} from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { ThemedInput } from "@/shared/components/ThemedInput";
import {
  PasswordStrengthBars,
  PasswordRequirementList,
  FormField,
} from "@/shared/components";
import { useChangePasswordForm } from "./useChangePasswordForm";
import { getEffectiveDir } from "@/shared/lib/direction";

interface ChangePasswordFormProps {
  onSubmit: (
    oldPassword: string,
    newPassword: string,
    newPasswordConfirm: string
  ) => Promise<void>;
  onSuccess: () => void;
  isRTL?: boolean;
  translate: (key: string) => string;
}

export function ChangePasswordForm({
  onSubmit,
  onSuccess,
  isRTL = true,
  translate,
}: ChangePasswordFormProps) {
  const {
    oldPassword,
    newPassword,
    confirmPassword,
    showOldPassword,
    showNewPassword,
    showConfirmPassword,
    isSubmitting,
    error,
    strength,
    passwordsMatch,
    canSubmit,
    setOldPassword,
    setNewPassword,
    setConfirmPassword,
    setShowOldPassword,
    setShowNewPassword,
    setShowConfirmPassword,
    handleFormSubmit,
    setSubmitError: _setSubmitError,
    setSubmitSuccess: _setSubmitSuccess,
  } = useChangePasswordForm({
    onSuccess,
    onSubmit,
    successMessage: translate("changePassword.successMessage"),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleFormSubmit(e);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* ── Old password ── */}
      <FormField label={translate("changePassword.oldPasswordLabel")}>
        <ThemedInput
          id="old-password"
          type={showOldPassword ? "text" : "password"}
          name="old-password"
          placeholder={translate("changePassword.oldPasswordPlaceholder")}
          value={oldPassword}
          onChange={(e) => setOldPassword(e.target.value)}
          autoComplete="current-password"
          suffixPosition="physical"
          suffix={
            <button
              type="button"
              onClick={() => setShowOldPassword(!showOldPassword)}
              className="transition-colors cursor-pointer"
              style={{ color: "var(--color-muted-text)" }}
              aria-label={
                showOldPassword
                  ? translate("changePassword.hidePassword")
                  : translate("changePassword.showPassword")
              }
            >
              {showOldPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          }
        />
      </FormField>

      {/* ── New password ── */}
      <FormField label={translate("changePassword.newPasswordLabel")}>
        <ThemedInput
          id="new-password"
          type={showNewPassword ? "text" : "password"}
          name="new-password"
          placeholder={translate("changePassword.newPasswordPlaceholder")}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          autoComplete="new-password"
          suffixPosition="physical"
          suffix={
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="transition-colors cursor-pointer"
              style={{ color: "var(--color-muted-text)" }}
              aria-label={
                showNewPassword
                  ? translate("changePassword.hidePassword")
                  : translate("changePassword.showPassword")
              }
            >
              {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          }
        />
      </FormField>

      {/* Strength bars */}
      {newPassword.length > 0 && (
        <PasswordStrengthBars score={strength.score} className="mt-2" />
      )}

      {/* Requirement checklist */}
      {newPassword.length > 0 && (
        <PasswordRequirementList
          met={strength.met}
          translate={translate}
          isRTL={isRTL}
        />
      )}

      {/* ── Confirm password ── */}
      <FormField
        label={translate("changePassword.confirmPasswordLabel")}
        error={confirmPassword.length > 0 && !passwordsMatch}
      >
        <ThemedInput
          id="confirm-password"
          type={showConfirmPassword ? "text" : "password"}
          name="confirm-password"
          placeholder={translate("changePassword.confirmPasswordPlaceholder")}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
          error={confirmPassword.length > 0 && !passwordsMatch}
          suffixPosition="physical"
          suffix={
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="transition-colors cursor-pointer"
              style={{ color: "var(--color-muted-text)" }}
              aria-label={
                showConfirmPassword
                  ? translate("changePassword.hidePassword")
                  : translate("changePassword.showPassword")
              }
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          }
        />
      </FormField>
      {confirmPassword.length > 0 && !passwordsMatch && (
        <p
          className="text-xs font-medium"
          style={{ color: "var(--destructive)" }}
          dir={getEffectiveDir()}
        >
          {translate("changePassword.errors.passwordsMismatch")}
        </p>
      )}

      {/* ── Error message ── */}
      {error && (
        <p
          className="text-sm font-medium"
          style={{ color: "var(--destructive)" }}
          role="alert"
          dir={getEffectiveDir()}
        >
          {error}
        </p>
      )}

      {/* ── Submit button — auto-width, aligned to end per Dashboard.html .btn
          (flex-end is direction-aware, so it sits on the inline-end in RTL). ── */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={!canSubmit}
          className={cn(
            "h-[38px] px-4 rounded-[10px]",
            "font-semibold text-[13px]",
            "inline-flex items-center justify-center gap-1.5 transition-all duration-200",
            "disabled:opacity-35 disabled:cursor-not-allowed",
          )}
          style={{
            background: "var(--foreground)",
            color: "var(--background)",
            boxShadow: "0 2px 12px rgba(26,25,23,0.18)",
            fontFamily: "var(--font-sans)",
          }}
        >
          <Check size={14} strokeWidth={2.4} />
          {isSubmitting
            ? translate("changePassword.submitting")
            : translate("changePassword.submitButton")}
        </button>
      </div>
    </form>
  );
}